import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
    AcademicTermListResponse,
    AcademicTermModel,
    AcademicTermYearModel,
    KkuWorkloadSyncResponse,
    StudyPeriodPayLoad,
} from "../../models/study-period/study-period.model";

const SKIP_GLOBAL_ERROR_DIALOG_HEADER = 'X-Skip-Global-Error-Dialog';

interface AcademicTermListParams {
    name?: string | null;
    year?: string | null;
    summary?: boolean;
    page?: number;
    limit?: number;
    sort?: string | null;
    direction?: string | null;
}

@Injectable({
    providedIn: "root",
})
export class StudyPeriodService {
    private apiUrl = "/academic_terms";

    #http = inject(HttpClient);



    createStudyPeriod(payload: StudyPeriodPayLoad): Observable<any> {
        return this.#http.post<StudyPeriodPayLoad>(this.apiUrl, payload, {
            headers: {
                [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true',
            },
        });
    }
    updateStudyPeriod(id: number, payload: StudyPeriodPayLoad): Observable<any> {
        return this.#http.put<StudyPeriodPayLoad>(`${this.apiUrl}/${id}`, payload, {
            headers: {
                [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true',
            },
        });
    }
    syncWorkload(id: number): Observable<KkuWorkloadSyncResponse> {
        return this.#http.post<KkuWorkloadSyncResponse>(`${this.apiUrl}/${id}/sync_workload`, {});
    }
    getSyncWorkloadStatus(id: number): Observable<KkuWorkloadSyncResponse> {
        return this.#http.get<KkuWorkloadSyncResponse>(`${this.apiUrl}/${id}/sync_workload_status`);
    }
    downloadSyncWorkloadErrors(id: number): Observable<Blob> {
        return this.#http.get(`${this.apiUrl}/${id}/sync_workload_errors`, {
            responseType: 'blob'
        });
    }
    deleteStudyPeriod(id: number): Observable<void> {
        return this.#http.delete<void>(`${this.apiUrl}/${id}`, {
            headers: {
                [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true',
            },
        });
    }
    getAcademicTerms(params?: AcademicTermListParams): Observable<AcademicTermModel[]> {
        return this.getAcademicTermsList(params).pipe(
            map((res) => this.normalizeAcademicTermsResponse(res))
        );
    }
    getAcademicTermById(id: number) {
        return this.#http.get<AcademicTermModel>(`${this.apiUrl}/${id}`);
    }
    getAcademicTermYears(): Observable<AcademicTermYearModel[]> {
        return this.#http.get<AcademicTermYearModel[]>(`${this.apiUrl}/years`);
    }
    serchAcademicTerms(params?: AcademicTermListParams): Observable<AcademicTermModel[]> {
        return this.getAcademicTermsList(params).pipe(
            map((res) => this.normalizeAcademicTermsResponse(res))
        );
    }

    getAcademicTermsList(params?: AcademicTermListParams): Observable<AcademicTermListResponse> {
        return this.#http.get<AcademicTermModel[] | AcademicTermListResponse>(this.apiUrl, {
            params: this.buildListParams(params)
        }).pipe(
            map((res) => this.normalizeAcademicTermsListResponse(res, params))
        );
    }

    private buildListParams(params?: AcademicTermListParams): HttpParams {
        let httpParams = new HttpParams();

        if (params?.name != null && params.name !== '') {
            httpParams = httpParams.set('name', params.name);
        }

        if (params?.year != null && params.year !== '') {
            httpParams = httpParams.set('year', params.year);
        }

        if (params?.summary != null) {
            httpParams = httpParams.set('summary', String(params.summary));
        }

        if (params?.page != null) {
            httpParams = httpParams.set('page', String(params.page));
        }

        if (params?.limit != null) {
            httpParams = httpParams.set('limit', String(params.limit));
        }

        if (params?.sort != null && params.sort !== '') {
            httpParams = httpParams.set('sort', params.sort);
        }

        if (params?.direction != null && params.direction !== '') {
            httpParams = httpParams.set('direction', params.direction);
        }

        return httpParams;
    }

    private normalizeAcademicTermsListResponse(
        res: AcademicTermModel[] | AcademicTermListResponse,
        params?: AcademicTermListParams
    ): AcademicTermListResponse {
        if (!Array.isArray(res)) {
            return res;
        }

        const limit = params?.limit ?? 10;
        const page = params?.page ?? 1;

        return {
            data: res,
            page,
            limit,
            total: res.length,
            total_pages: Math.max(1, Math.ceil(res.length / limit)),
        };
    }

    private normalizeAcademicTermsResponse(
        res: AcademicTermModel[] | AcademicTermListResponse
    ): AcademicTermModel[] {
        if (Array.isArray(res)) {
            return res;
        }

        return res.data;
    }
}
